// UserBootstrap.tsx
import * as React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Alert, AlertTitle, Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { createApiClient, useApiClient } from "../components/useApiClient";
import { useSafeRequest } from "../components/useSafeRequest"; 
import { useUser } from "../components/UserContext";
import { warmUpApi, warmUpSql } from "../components/warmup";
import { UserRecord, ClientAccess } from "../models/UserProfile";
import {
    InteractionRequiredAuthError,
    InteractionStatus
} from "@azure/msal-browser";
import { loginRequest } from "../authConfig"; 

// ---------- helpers ----------
const pickDefaultClientId = (u?: UserRecord | null): number | null =>
    u?.defaultClientId ??
    u?.clients?.[0]?.clientId ??
    null;

const backoffDelay = (attempt: number, base = 400, cap = 12000) => {
    const exp = Math.min(cap, base * Math.pow(2, Math.max(0, attempt - 1)));
    const jitter = 0.5 + Math.random(); // 0.5–1.5
    return Math.round(exp * jitter);
};

type Phase = "idle" | "loading" | "retrying" | "reauthenticating" | "ready" | "stopped";

export const UserBootstrap: React.FC<
    React.PropsWithChildren<{ blockUntilReady?: boolean }>
> = ({ children, blockUntilReady = true }) => {
    const isAuthenticated = useIsAuthenticated();
    const { getAccessToken, user, setUser, activeClientId, setActiveClientId, setActiveTenantId, setIsViewOnly, setHasClientLevelRole, setDefaultPortfolioId } = useUser();

    // keep a stable axios client; read latest clientId from a ref in the interceptor
    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId ?? null; }, [activeClientId]);
    const api = React.useMemo(
        () => createApiClient({ getAccessToken, getClientId: () => clientIdRef.current }, { timeoutMs: 12000 }),
        [getAccessToken]
    );

    const { instance, accounts, inProgress } = useMsal();

    // retry state
    const [phase, setPhase] = React.useState<Phase>("idle");
    const [attempt, setAttempt] = React.useState(0);
    const [nextMs, setNextMs] = React.useState<number | null>(null);
    const [lastError, setLastError] = React.useState<string | null>(null);

    const countdownTimer = React.useRef<number | null>(null);
    const retryTimer = React.useRef<number | null>(null);
    const abortRef = React.useRef<AbortController | null>(null);
    const mountedRef = React.useRef(true);
    React.useEffect(() => () => { mountedRef.current = false; }, []);

    const clearTimers = () => {
        if (retryTimer.current) { window.clearTimeout(retryTimer.current); retryTimer.current = null; }
        if (countdownTimer.current) { window.clearInterval(countdownTimer.current); countdownTimer.current = null; }
        setNextMs(null);
    };

    const scheduleCountdown = (ms: number) => {
        setNextMs(ms);
        if (countdownTimer.current) window.clearInterval(countdownTimer.current);
        const startedAt = Date.now();
        countdownTimer.current = window.setInterval(() => {
            const left = ms - (Date.now() - startedAt);
            setNextMs(left > 0 ? left : 0);
            if (left <= 0 && countdownTimer.current) {
                window.clearInterval(countdownTimer.current);
                countdownTimer.current = null;
            }
        }, 200);
    };

    const reauthenticate = React.useCallback(async () => {

        clearTimers();
        abortRef.current?.abort();

        // Critical guard:
        // Never initiate interactive authentication from MSAL's hidden iframe.
        if (window.self !== window.top) {
            return;
        }

        // Don't start another MSAL interaction if one is already underway.
        if (inProgress !== InteractionStatus.None) {
            return;
        }

        setPhase("reauthenticating");
        setLastError(
            "Your Microsoft session has expired. Please sign in again."
        );

        const account =
            instance.getActiveAccount() ??
            accounts[0];

        await instance.acquireTokenRedirect({
            ...loginRequest,
            account: account ?? undefined,
            prompt: "login"
        });

    }, [instance, accounts, inProgress]);

    const getClientInfo = (clients: ClientAccess[] | undefined, targetClientId: number | null) => {

        if (clients && targetClientId != null) {
            const client = clients.find(client => client.clientId === targetClientId);

            if (client) {
                return client;
            }
        }

        return null;
    };

    // 1) Make tryLoadUser stable enough. Do NOT depend on `attempt`.
    const tryLoadUser = React.useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setPhase(p => (p === "idle" ? "loading" : "retrying"));
        setLastError(null);

        try {
            const warmA = await warmUpApi();
            const warmB = await warmUpSql();

            const res = await api.get<UserRecord>("user", { signal: controller.signal });
            if (!mountedRef.current) return;

            setUser(res.data);

            //License State
            const isReadOnly = !res.data?.license?.canEdit;
            setIsViewOnly(isReadOnly);

            //Tenant
            const inferred =
                res.data.defaultTenantId ??
                res.data.tenants?.find(t => t.isDefault)?.tenantId ??
                res.data.tenants?.[0]?.tenantId ??
                null;

            setActiveTenantId(inferred);

            const picked = pickDefaultClientId(res.data);
            if (picked != null && picked !== activeClientId) setActiveClientId(picked);

            //Client Role
            if (res.data.clients) {
                const clientInfo = getClientInfo(res.data.clients, picked);
                const hasClientLvlRole = clientInfo?.hasClientWideRole ?? false;
                setHasClientLevelRole(hasClientLvlRole);
                setDefaultPortfolioId(clientInfo?.defaultPortfolioId ?? 0);
            } else {
                setHasClientLevelRole(false);
                setDefaultPortfolioId(0);
            }
           
            clearTimers();
            setPhase("ready");
        } catch (err: any) {

            if (!mountedRef.current) return;

            // Request deliberately cancelled
            if (
                controller.signal.aborted ||
                err?.name === "CanceledError" ||
                err?.code === "ERR_CANCELED"
            ) {
                return;
            }

            // ----------------------------------------
            // MSAL says user interaction is required
            // ----------------------------------------
            if (
                err instanceof InteractionRequiredAuthError ||
                err?.name === "InteractionRequiredAuthError" ||
                err?.errorCode === "interaction_required" ||
                err?.errorCode === "login_required" ||
                err?.errorCode === "consent_required"
            ) {
                clearTimers();

                setPhase("reauthenticating");
                setLastError(
                    "Your Microsoft session has expired. Please sign in again."
                );

                await reauthenticate();

                return;
            }

            const status = err?.response?.status as number | undefined;

            // ----------------------------------------
            // API authentication failure
            // ----------------------------------------
            if (status === 401) {
                clearTimers();

                setPhase("reauthenticating");
                setLastError(
                    "Your authentication session could not be verified."
                );

                await reauthenticate();

                return;
            }

            // ----------------------------------------
            // Retry transient service errors only
            // ----------------------------------------
            const retryable =
                status == null ||
                status === 408 ||
                status === 429 ||
                status === 502 ||
                status === 503 ||
                status === 504;

            if (!retryable) {
                clearTimers();

                setLastError(
                    status
                        ? `The service returned HTTP ${status}.`
                        : "An unexpected error occurred."
                );

                setPhase("stopped");

                return;
            }

            const msg = status
                ? `The service responded with HTTP ${status}. We’ll retry automatically.`
                : `We’re having trouble reaching the service. It may be waking up—retrying…`;

            setLastError(msg);

            setAttempt(a => {
                const nextAttempt = a + 1;
                const delay = backoffDelay(nextAttempt);

                clearTimers();
                scheduleCountdown(delay);

                retryTimer.current = window.setTimeout(() => {
                    if (mountedRef.current) {
                        void tryLoadUser();
                    }
                }, delay);

                setPhase("retrying");

                return nextAttempt;
            });
        }
    }, [api, activeClientId, setUser, setActiveClientId, reauthenticate]);

    // 2) Init effect: depends ONLY on auth; no cleanup here.
    const didRunRef = React.useRef(false);

    React.useEffect(() => {

        if (!isAuthenticated) {
            didRunRef.current = false;
            return;
        }

        // Wait for MSAL to finish redirect/login processing
        if (inProgress !== InteractionStatus.None) {
            return;
        }

        if (didRunRef.current) {
            return;
        }

        // Don't bootstrap the full app from an MSAL hidden iframe
        if (window.self !== window.top) {
            return;
        }

        didRunRef.current = true;

        setAttempt(0);

        void tryLoadUser();

    }, [isAuthenticated, inProgress]);

    // 3) Unmount-only cleanup so timers aren’t nuked on every re-render
    React.useEffect(() => {
        return () => {
            clearTimers();
            abortRef.current?.abort();
        };
    }, []);


    // manual controls
    const retryNow = () => {
        clearTimers();
        setAttempt((a) => a + 1);
        void tryLoadUser();
    };
    const stopRetrying = () => {
        clearTimers();
        abortRef.current?.abort();
        setPhase("stopped");
    };

    const ready = isAuthenticated && user && (!blockUntilReady || activeClientId != null) && phase === "ready";

    // Small status surface (only while not ready)
    if (!ready) {
        return (
            <Stack sx={{ p: 2, gap: 2 }}>
                <Alert severity={phase === "stopped" ? "warning" : "info"} icon={false}>
                    <AlertTitle sx={{ mb: 1 }}>
                        {phase === "loading" && "Loading your profile…"}
                        {phase === "retrying" && "Connecting…"}
                        {phase === "stopped" && "Connection paused"}
                        {phase === "idle" && "Starting…"}
                    </AlertTitle>

                    <Stack spacing={1}>
                        {lastError && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {lastError}
                            </Typography>
                        )}

                        <Box>
                            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                                Attempt {Math.max(attempt, 1)} {phase === "retrying" && nextMs != null ? `• retrying in ${Math.ceil((nextMs ?? 0) / 1000)}s` : ""}
                            </Typography>
                            <LinearProgress />
                        </Box>

                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" onClick={retryNow} disabled={phase === "loading"}>
                                Retry now
                            </Button>
                            {phase !== "stopped" ? (
                                <Button variant="text" size="small" onClick={stopRetrying}>Pause</Button>
                            ) : (
                                <Button variant="text" size="small" onClick={retryNow}>Resume</Button>
                            )}
                        </Stack>
                    </Stack>
                </Alert>

                {/* Optional: warm message for first-time cold start */}
                <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "center" }}>
                    If this is the first use the system may take a few seconds to wake up.
                </Typography>
            </Stack>
        );
    }

    // Ready → render the app
    return <>{children}</>;
};
